import { CalculateEVRequest, OutcomeInput, SportsbookInput } from '../data/evTypes';

/**
 * Hardcoded NBA OfferIDs that map to odds chart types in the backend.
 * These MUST match the GUIDs in OddsChartsService.cs OfferToChartMapping.
 */
const NBA_OFFER_IDS: Record<string, string> = {
  // Beta distribution (continuous stats)
  points: 'AA4DD9A9-6AEC-44F9-8AE0-3599520B9351',
  pts: 'AA4DD9A9-6AEC-44F9-8AE0-3599520B9351',
  pra: 'CF42A171-7426-40C7-BD27-412A94B7115C',
  pr: '8375152E-5B9B-4263-BF4B-E659941F9532',
  'p+r': '8375152E-5B9B-4263-BF4B-E659941F9532',
  pa: '2D215A6A-3DFF-4C77-A67E-902C79C631AD',
  'p+a': '2D215A6A-3DFF-4C77-A67E-902C79C631AD',
  ra: '185A1DE3-5B32-4FA1-92B7-37F1433C6FEE',
  'r+a': '185A1DE3-5B32-4FA1-92B7-37F1433C6FEE',

  // Poisson distribution (count stats)
  rebounds: '274FDE84-066E-46EE-96BB-E76C98E1115A',
  reb: '274FDE84-066E-46EE-96BB-E76C98E1115A',
  assists: '3866E204-19C2-4513-AC9B-CE06A89FFC1D',
  ast: '3866E204-19C2-4513-AC9B-CE06A89FFC1D',
  threes: '550B8FC7-D7E7-46AB-8FA6-FAF07EA98372',
  '3pm': '550B8FC7-D7E7-46AB-8FA6-FAF07EA98372',
  steals: '259ECBA3-2D55-4B2A-8B9E-490C2EDEA50C',
  stl: '259ECBA3-2D55-4B2A-8B9E-490C2EDEA50C',
  blocks: '354EF21D-5B64-42B8-8A07-2DC7D6EE3476',
  blk: '354EF21D-5B64-42B8-8A07-2DC7D6EE3476',
  turnovers: '358C483B-0362-4C62-AD25-080AEDD4F589',
  tov: '358C483B-0362-4C62-AD25-080AEDD4F589',
};

/**
 * Get the correct OfferId UUID for a stat type.
 * Returns the hardcoded GUID that maps to the backend odds chart.
 */
function getOfferIdForStat(stat: string): string {
  const normalized = stat.toLowerCase().replace(/[\s\-_]/g, '');
  const offerId = NBA_OFFER_IDS[normalized];
  if (offerId) {
    return offerId;
  }
  // Fallback: generate a random UUID (won't work for stokastic prob, but market prob might still work)
  return crypto.randomUUID();
}

/**
 * Map of canonical stat keys to all possible stat name variations in the data.
 * Used to match stats from changedStats array to actual prop stat names.
 */
const STAT_NAME_ALIASES: Record<string, string[]> = {
  // NBA stats
  points: ['points', 'pts'],
  rebounds: ['rebounds', 'reb'],
  assists: ['assists', 'ast'],
  threes: ['3pm', '3pointers', 'threes', 'three'],
  pra: ['pra', 'ptsrebsasts', 'points+rebounds+assists', 'pointsreboundsassists'],
  pr: ['pr', 'p+r', 'pointsrebounds', 'points+rebounds'],
  pa: ['pa', 'p+a', 'pointsassists', 'points+assists'],
  ra: ['ra', 'r+a', 'reboundsassists', 'rebounds+assists'],
  // NHL stats
  goals: ['goals', 'totalplayergoals', 'playergoals', 'g'],
  sog: ['sog', 'shotsongoal', 'totalshots', 'shots'],
  saves: ['saves', 'goaliesaves', 'sv'],
  blocks: ['blocks', 'blockedshots', 'blk'],
  // NHL points (different from NBA points - maps to 'totalpoints' in props)
  // Includes 'pts' since transformPropsData normalizes "Total Points" to "PTS"
  nhlpoints: ['totalpoints', 'nhlpoints', 'pts'],
  // NHL assists (different key to avoid collision with NBA assists)
  // Includes 'ast' since transformPropsData normalizes "Total Assists" to "AST"
  nhlassists: ['totalassists', 'nhlassists', 'ast'],
};

/**
 * Stat dependency map: when a stat changes, which combo stats need recalculation.
 */
const STAT_DEPENDENCIES: Record<string, string[]> = {
  points: ['pra', 'pr', 'pa'],
  rebounds: ['pra', 'pr', 'ra'],
  assists: ['pra', 'pa', 'ra'],
  // NFL (normalized display codes; combos follow their components)
  passyds: ['pass+rushyds'],
  rushyds: ['pass+rushyds', 'rush+recyds'],
  recyds: ['rush+recyds'],
  rushtds: ['anytd', 'rush+rectds'],
  rectds: ['anytd', 'rush+rectds'],
};

/**
 * Normalize stat name for comparison (lowercase, remove spaces/hyphens/underscores).
 */
function normalizeStatName(stat: string): string {
  return stat.toLowerCase().replace(/[\s\-_]/g, '');
}

/**
 * Get the canonical stat key for a given stat name.
 * Returns the stat key if found, or the normalized name if not.
 */
function getCanonicalStatKey(statName: string): string {
  const normalized = normalizeStatName(statName);
  for (const [key, aliases] of Object.entries(STAT_NAME_ALIASES)) {
    if (aliases.includes(normalized)) {
      return key;
    }
  }
  return normalized;
}

/**
 * Check if a stat name matches any of the canonical stats we're looking for.
 * Instead of resolving to a single canonical key (which fails for shared aliases
 * like 'pts' appearing in both NBA 'points' and NHL 'nhlpoints'), this checks
 * if the normalized stat name appears in ANY target canonical stat's alias list.
 */
function statMatchesAny(statName: string, canonicalStats: Set<string>): boolean {
  const normalized = normalizeStatName(statName);

  for (const targetCanonical of Array.from(canonicalStats)) {
    const aliases = STAT_NAME_ALIASES[targetCanonical];
    if (aliases?.includes(normalized)) {
      return true;
    }
  }

  // Fallback: direct match with a canonical stat key
  return canonicalStats.has(normalized);
}

/**
 * Get all stats that need recalculation when a given stat changes.
 * Includes the original stat plus any dependent combo stats.
 */
export function getStatsWithDependencies(changedStats: string[]): Set<string> {
  const statsToInclude = new Set<string>();

  for (const stat of changedStats) {
    const canonical = getCanonicalStatKey(stat);
    statsToInclude.add(canonical);

    // Add any dependent combo stats
    const deps = STAT_DEPENDENCIES[canonical];
    if (deps) {
      deps.forEach((dep) => statsToInclude.add(dep));
    }
  }

  return statsToInclude;
}

interface Sportsbook {
  book: string; // sportsbook code (e.g., "draftkings", "fanduel")
  line: number;
  americanOdds?: string;
  marketWin?: number | null; // market true win probability
  side?: 'Over' | 'Under';
}

interface PlayerProp {
  stat: string;
  projection?: number;
  offerId?: string; // UUID from original API response
  overProbabilities?: Record<string, number> | null; // Feed's P(over) grid (NFL)
  sportsbooks?: Sportsbook[];
}

interface PlayerWithProps {
  id?: string;
  props?: PlayerProp[];
}

/**
 * Build outcomes for EV calculation from a player's props.
 * Includes dependent stats (e.g., PRA when PTS/REB/AST change).
 *
 * @param player - Player data with props
 * @param changedStats - Stats that were modified (e.g., ['points', 'rebounds'])
 * @param getProjection - Function to get the current projection value for a stat
 */
export function buildOutcomesForPlayer(
  player: PlayerWithProps,
  changedStats: string[],
  getProjection: (stat: string) => number
): OutcomeInput[] {
  if (!player.props?.length) {
    return [];
  }

  const statsToInclude = getStatsWithDependencies(changedStats);
  const outcomes: OutcomeInput[] = [];

  for (const prop of player.props) {
    // Check if this stat should be included using canonical matching
    const matches = statMatchesAny(prop.stat, statsToInclude);

    if (!matches) {
      continue;
    }

    // Skip if no sportsbooks
    if (!prop.sportsbooks?.length) {
      continue;
    }

    const projection = getProjection(prop.stat);

    // Use offerId from original API response, or fall back to stat lookup
    const offerId = prop.offerId || getOfferIdForStat(prop.stat);

    // Filter to only sportsbooks with true odds
    const validSportsbooks = prop.sportsbooks.filter(
      (sb) => sb.book && sb.americanOdds && sb.marketWin && sb.marketWin > 0
    );

    if (validSportsbooks.length === 0) {
      continue;
    }

    // Determine the side from the first valid sportsbook (all sportsbooks in a prop have the same side)
    const side = validSportsbooks[0].side;
    const label = (side?.toLowerCase() === 'under' ? 'Under' : 'Over') as 'Over' | 'Under';

    outcomes.push({
      offerId,
      statType: prop.stat,
      projection,
      label,
      // Echo the feed's distribution so an untouched projection reproduces the table's EV exactly, and an
      // edited one is rescaled on the same shape rather than re-priced on a different model.
      overProbabilities: prop.overProbabilities ?? undefined,
      baselineProjection: prop.projection,
      sportsbooks: validSportsbooks.map((sb) => ({
        code: sb.book,
        line: sb.line,
        americanOdds: sb.americanOdds || '',
        marketTrueWinProbability: sb.marketWin ?? null,
      })),
    });
  }

  return outcomes;
}

/**
 * Build a complete EV calculation request for a player.
 *
 * @param player - Player data with props
 * @param changedStats - Stats that were modified
 * @param getProjection - Function to get the current projection value for a stat
 * @param stokWeight - Stokastic weight (default 0.2)
 * @param marketWeight - Market weight (default 0.8)
 */
export function buildEVRequest(
  player: PlayerWithProps,
  changedStats: string[],
  getProjection: (stat: string) => number,
  stokWeight: number = 0.2,
  marketWeight: number = 0.8,
  league?: string
): CalculateEVRequest {
  return {
    outcomes: buildOutcomesForPlayer(player, changedStats, getProjection),
    stokWeight,
    marketWeight,
    ...(league ? { league } : {}),
  };
}
